import SharedAutocomplete from '../../../shared/shared-autocomplete';
import { useEffect, useRef, useState } from 'react';
import CalendarGrid from '../../user-management/user-details/calendars/calendar-grid';
import { UserKeys, getCalendars } from '../../../api/users-api';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { pagesConfig } from '../../../shared/pages-config';
import { useUserSettings } from '../../../utils/use-user-settings';
import { Messages } from '../../../utils/message-constants';
import SimpleBreadcrumbsPage from '../../../shared/simple-breadcrumbs-page';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { CalendarRoles } from '../../../utils/roles/client-roles';
import { useChangeLogGate } from '../../../shared/hooks/use-change-log-gate';
import Box from '@mui/material/Box';

export default function CalendarsPage() {
  const { calendarRoles, administrationRoles, changeLogRoles, primaryDomain } =
    useUserSettings();

  const [autoCompleteValue, setAutoCompleteValue] = useState('');
  const fetched = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const hasUserEmail = searchParams.has('userEmail');
  const userEmail = searchParams.get('userEmail');
  const {
    needsChangeLog,
    canFetch,
    setCanFetch,
    confirmedChangeLogMessage,
    changeLogMessage,
    setChangeLogMessage,
    openChangeLogDialog,
    openDialog,
    confirm,
    dismiss,
    reset,
  } = useChangeLogGate({
    permissionValue: CalendarRoles.ViewCalendars.value,
    changeLogRoles,
    initialOpen: false,
  });

  // If the page is loaded with a userEmail already in the URL (e.g. browser refresh),
  // auto-open the dialog so the user still provides a changelog message before data loads
  useEffect(() => {
    if (userEmail && needsChangeLog && !confirmedChangeLogMessage) {
      openDialog();
    }
  }, []);

  useEffect(() => {
    if (!hasUserEmail) {
      setAutoCompleteValue('');
      // Reset changelog state so the next search requires a new message
      reset();
      fetched.current = false;
    }
  }, [hasUserEmail]);

  const handleAutoCompleteSelection = (value: string) => {
    if (value === '') {
      setSearchParams({});
      reset();
    } else {
      setCanFetch(false);
      reset();
      setSearchParams({ userEmail: value });
      if (needsChangeLog) {
        openDialog();
      } else {
        setCanFetch(true);
      }
    }
  };

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [UserKeys.Calendars, userEmail, confirmedChangeLogMessage],
    queryFn: () => getCalendars(userEmail!, confirmedChangeLogMessage),
    enabled: userEmail !== null && userEmail !== '' && canFetch,
    meta: {
      errorMessage: Messages.Errors.Network.Loading('calendars'),
    },
  });

  if (isLoading) {
    fetched.current = true;
  }

  return (
    <SimpleBreadcrumbsPage
      breadcrumbs={[{ label: pagesConfig.calendars.name }]}
      bookmarkKey={pagesConfig.calendars.value}
      topContent={
        <Box sx={{ width: 400, bgcolor: 'background.paper', borderRadius: 1 }}>
          <SharedAutocomplete
            sources={['users', 'calendars']}
            autocompleteValue={autoCompleteValue}
            setAutoCompleteValue={setAutoCompleteValue}
            onSelection={handleAutoCompleteSelection}
          />
        </Box>
      }
      heightOfTopContent={48}
    >
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={dismiss}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() =>
          confirm(() => setSearchParams({ userEmail: userEmail || '' }))
        }
        mutateLoading={false}
      />
      <CalendarGrid
        userEmail={userEmail || ''}
        primaryDomain={primaryDomain}
        calendarRoles={calendarRoles}
        changeLogRoles={changeLogRoles}
        administrationRoles={administrationRoles}
        data={data || []}
        isLoading={isLoading}
        isFetching={isFetching}
        isAllUsers={true}
        fetched={fetched.current}
        onOpenChangeLogDialog={
          !!userEmail && needsChangeLog && !canFetch ? openDialog : undefined
        }
      />
    </SimpleBreadcrumbsPage>
  );
}
